import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatError } from '../services/api';
import axios from '../services/api';
import { API } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { Spinner, ShieldCheck, EnvelopeSimple, Lock } from '@phosphor-icons/react';
import { track, resetActivationFlags, ACTIVATION_EVENTS } from '../services/activation';

/*
  Public trial registration page.
  - No invite required. Anyone can sign up.
  - Existing-account detection still takes priority (ACCOUNT_EXISTS 409).
  - Legacy invite links (?invite=TOKEN) still work: if valid, we pre-fill and
    lock the email, and the backend consumes the invite. If invalid/expired,
    we silently fall through to normal public signup — no dead-end screen.
*/
const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');
  // Stage 11 — Referral code flows in via /register?ref=CODE. We keep it in
  // state so even if the user edits the URL mid-flow we use the version that
  // was present on first paint.
  const [referralCode] = useState(() => {
    const raw = (searchParams.get('ref') || '').trim().toUpperCase();
    return /^[A-Z0-9]{4,12}$/.test(raw) ? raw : '';
  });
  const { register, user } = useAuth();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');  // Stage 2-P1
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Optional invite pre-fill (soft — never blocks signup)
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRecognised, setInviteRecognised] = useState(false);

  // "Account already exists" state — overrides everything else when true.
  const [accountExists, setAccountExists] = useState(false);
  const [existingEmail, setExistingEmail] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [checkingEmail, setCheckingEmail] = useState(false);

  const goToLogin = () => {
    navigate('/login', { state: { prefillEmail: existingEmail || email } });
  };

  const getOnboardingRoute = async () => {
    try {
      const { data: cfg } = await axios.get(`${API}/onboarding-lite/config`);
      return cfg.lite_enabled ? '/onboarding-lite' : '/onboarding';
    } catch { return '/onboarding'; }
  };

  useEffect(() => {
    if (user) {
      if (user.onboarding_completed) navigate('/home');
      else getOnboardingRoute().then(route => navigate(route));
    }
  }, [user, navigate]);

  // Soft invite pre-fill — best-effort, never blocks.
  useEffect(() => {
    if (!inviteToken) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/validate-invite?token=${inviteToken}`);
        if (data?.email) {
          setInviteEmail(data.email);
          setEmail(data.email);
          setInviteRecognised(true);
          // Precheck: if invited email already has account, route to sign in.
          try {
            const { data: chk } = await axios.post(`${API}/auth/check-email`, { email: data.email });
            if (chk?.exists) {
              setExistingEmail(data.email);
              setAccountExists(true);
            }
          } catch { /* best-effort */ }
        }
      } catch { /* invalid/expired invite — silently fall through to public signup */ }
    })();
  }, [inviteToken]);

  // Stage 11 — fire a funnel event the first time a user lands on /register?ref=CODE.
  // Non-blocking; duplicates are fine (deduped server-side by the funnel).
  useEffect(() => {
    if (!referralCode) return;
    try { track('referral_signup_started', { code: referralCode, surface: 'register_page' }); } catch { /* noop */ }
  }, [referralCode]);

  const handleEmailBlur = async () => {
    const value = (email || '').trim().toLowerCase();
    if (!value || !/\S+@\S+\.\S+/.test(value)) return;
    try {
      setCheckingEmail(true);
      const { data } = await axios.post(`${API}/auth/check-email`, { email: value });
      if (data?.exists) {
        setExistingEmail(value);
        setAccountExists(true);
      }
    } catch {
      // Precheck is best-effort; backend will still return 409 ACCOUNT_EXISTS.
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register(email, password, name, inviteToken || undefined, referralCode || undefined);
      // A new user just came into existence — clear any stale activation flags
      // left on this device by a previous session so the new user's "firsts" fire.
      resetActivationFlags();
      track(ACTIVATION_EVENTS.REGISTER_COMPLETE, {
        invite_used: !!inviteToken,
        from_invite_email: !!inviteEmail,
        referral_code_used: !!referralCode,
        reason: reason || 'unspecified',
      });
      // Stage 2-P1 — persist reason to profile preferences for funnel analytics.
      if (reason) {
        try {
          await axios.put(`${API}/profile/preferences`, { signup_reason: reason });
        } catch { /* non-blocking */ }
      }
      const route = await getOnboardingRoute();
      navigate(route);
    } catch (err) {
      const payload = err.response?.data;
      if (err.response?.status === 409 && payload?.error_code === 'ACCOUNT_EXISTS') {
        setExistingEmail(email.trim().toLowerCase());
        setAccountExists(true);
        return;
      }
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Account already exists — friendly sign-in hand-off.
  if (accountExists) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex flex-col items-center justify-center px-8 safe-top">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md text-center"
          data-testid="account-exists-panel"
        >
          <img src="/kindra-logo.png" alt="Kindra" className="h-20 mx-auto object-contain mb-8" />
          <div className="bg-white border border-slate-200 rounded-xl p-8">
            <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <EnvelopeSimple size={24} className="text-[#235347]" weight="duotone" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              You already have an account
            </h2>
            <p className="text-slate-500 text-sm leading-relaxed mb-6" data-testid="account-exists-message">
              It looks like <span className="text-slate-700 font-medium">{existingEmail || email}</span> is already registered.
            </p>
            <div className="space-y-3">
              <button
                onClick={goToLogin}
                className="block w-full bg-[#235347] text-white rounded-lg px-6 py-3 font-medium text-sm hover:bg-[#1A3E35] transition-colors"
                data-testid="sign-in-instead-btn"
              >
                Sign in instead
              </button>
              <a
                href={`mailto:hello@kindraai.dev?subject=Kindra%20password%20help&body=Hi%2C%20I%20need%20help%20signing%20in%20to%20my%20Kindra%20account%20(${encodeURIComponent(existingEmail || email)}).`}
                className="block w-full text-slate-500 text-sm hover:text-[#235347] transition-colors py-2"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Public registration form.
  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col items-center justify-center px-8 safe-top">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <img src="/kindra-logo.png" alt="Kindra" className="h-24 mx-auto object-contain mb-6" />
          <h2 className="text-2xl font-light text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="register-heading">
            Start your free trial
          </h2>
          <p className="text-slate-500 text-sm mt-2">
            Free for 30 days · No credit card required
          </p>
          <p className="text-slate-400 text-xs mt-1.5">
            Cancel anytime before billing · Subscription choice later
          </p>
          {inviteRecognised && (
            <div className="inline-flex items-center justify-center gap-1.5 mt-3 bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1">
              <ShieldCheck size={12} className="text-emerald-600" weight="bold" />
              <p className="text-emerald-700 text-xs font-medium">Invited access recognised</p>
            </div>
          )}
          {referralCode && !inviteRecognised && (
            <div
              className="inline-flex items-center justify-center gap-1.5 mt-3 bg-[#F4F0E6] border border-[#E6DEC9] rounded-full px-3 py-1"
              data-testid="referral-bonus-pill"
            >
              <ShieldCheck size={12} className="text-[#235347]" weight="bold" />
              <p className="text-[#235347] text-xs font-medium">
                Referral <span className="font-mono tracking-wide">{referralCode}</span> · +30 days free after signup
              </p>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm" data-testid="register-error">
              {error}
            </div>
          )}
          <input
            data-testid="register-name"
            type="text"
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-white border border-slate-200 rounded-xl px-5 py-4 focus:ring-2 focus:ring-[#235347]/20 focus:border-[#235347] outline-none transition-all"
            required
          />
          <div>
            <input
              data-testid="register-email"
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={handleEmailBlur}
              className="w-full bg-white border border-slate-200 rounded-xl px-5 py-4 focus:ring-2 focus:ring-[#235347]/20 focus:border-[#235347] outline-none transition-all"
              required
              readOnly={!!inviteEmail}
            />
            {inviteEmail && (
              <p className="text-xs text-slate-400 mt-1 ml-1">This invite is locked to this email address</p>
            )}
          </div>
          <input
            data-testid="register-password"
            type="password"
            placeholder="Password (min 8 characters)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-white border border-slate-200 rounded-xl px-5 py-4 focus:ring-2 focus:ring-[#235347]/20 focus:border-[#235347] outline-none transition-all"
            minLength={8}
            required
          />
          {/* Stage 2-P1 — Why are you here? Optional but tracks intent for funnel analytics. */}
          <div>
            <label className="text-[12px] font-medium text-slate-500 ml-1 mb-1.5 block tracking-wider uppercase">
              Why you're here <span className="text-slate-300 normal-case">(optional)</span>
            </label>
            <select
              data-testid="register-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full bg-white border border-slate-200 rounded-xl px-5 py-4 focus:ring-2 focus:ring-[#235347]/20 focus:border-[#235347] outline-none transition-all text-slate-700 appearance-none"
              style={{ backgroundImage: 'url("data:image/svg+xml;charset=US-ASCII,%3Csvg width=\'12\' height=\'8\' viewBox=\'0 0 12 8\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M1 1l5 5 5-5\' stroke=\'%2364748B\' stroke-width=\'1.5\' fill=\'none\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1.25rem center' }}
            >
              <option value="">Select a reason</option>
              <option value="understand_results">Understand my results</option>
              <option value="understand_symptoms">Understand my symptoms</option>
              <option value="prep_appointments">Prepare for appointments</option>
              <option value="manage_condition">Manage a condition</option>
              <option value="medication_support">Medication support</option>
              <option value="general_clarity">General health clarity</option>
            </select>
          </div>
          <button
            data-testid="register-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-[#235347] text-white rounded-full px-6 py-4 font-medium text-base hover:bg-[#1A3F36] transition-all disabled:opacity-50 flex items-center justify-center gap-2 mt-2"
          >
            {loading && <Spinner size={20} className="animate-spin" />}
            Create account
          </button>
          <p className="text-center text-xs text-slate-400 flex items-center justify-center gap-1.5 pt-1">
            <Lock size={12} /> Private by design · you can delete or export any time
          </p>
        </form>

        <p className="text-center mt-8 text-slate-500 text-sm">
          Already have an account?{' '}
          <button onClick={() => navigate('/login')} className="text-[#235347] font-medium hover:underline underline-offset-2" data-testid="nav-signin-link">
            Sign in
          </button>
        </p>
      </motion.div>
    </div>
  );
};

export default RegisterPage;
